import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'
import {
  mdiMagnify,
  mdiSitemap,
  mdiImport,
  mdiShape,
  mdiRobotOutline,
  mdiArrowExpandAll,
  mdiTextRecognition,
  mdiVectorCombine,
  mdiTagOutline,
  mdiTagMultipleOutline,
  mdiDatabaseSearch,
  mdiLinkVariant,
  mdiFileArrowLeftRight,
  mdiFunction,
  mdiCloudSearch,
  mdiEmail,
  mdiChartBar,
  mdiBroom,
  mdiEmailFastOutline,
  mdiTimerSand,
  mdiAlertOctagon,
  mdiSleep,
  mdiShieldCheck,
  mdiShieldAccount,
  mdiShoePrint,
  mdiDatabaseSync,
  mdiFileDocumentOutline,
  mdiVectorPolygon,
  mdiCheckDecagram,
  mdiFileChart,
  mdiMapMarker,
  mdiAccountGroupOutline,
  mdiCrosshairsGps,
  mdiScaleBalance,
  mdiMessageText,
  mdiZipBox,
  mdiMapMarkerCheck,
  mdiFileFind,
  mdiTableColumn,
  mdiFinance,
  mdiFloorPlan,
  mdiRulerSquare,
  mdiChartLine,
  mdiChartBox,
  mdiCalendarClock,
  mdiWaveform,
  mdiInboxArrowDown,
  mdiMap,
  mdiTableSync,
  mdiWhatsapp,
  mdiSignatureFreehand,
  mdiCallSplit,
} from '@mdi/js'

// Types based on the backend API specification
export interface JobResponse {
  uid: string
  type: string
  properties?: Record<string, unknown>
  priority: number
  nb_retries?: number
  attempt_count?: number
  scheduled_at: string // ISO timestamp
  final_status?: 'success' | 'failed' | 'cancelled'
  created_at: string // ISO timestamp
  updated_at: string // ISO timestamp
  execution_count?: number
  last_execution?: JobExecutionResponse
  executions?: JobExecutionResponse[]
}

export interface JobExecutionResponse {
  uid: string
  job_uid?: string
  status: 'pending' | 'running' | 'success' | 'failed' | 'cancelled' | 'lost' | 'manual'
  time_spent_ms?: number
  output?: Record<string, unknown>
  error?: Record<string, unknown>
  created_at: string // ISO timestamp
  updated_at: string // ISO timestamp
  logs?: JobExecutionLogResponse[]
}

export interface JobExecutionLogResponse {
  message: string
  attrs: Record<string, unknown>
  level: number
  time: string // ISO timestamp
}

export interface CreateJobRequest {
  type: string
  properties?: Record<string, unknown>
}

export interface ListJobsResponse {
  jobs: JobResponse[]
  /**
   * How many rows `hide_noop` removed from the total (spec 2026-09-05-08 §E).
   * 0 when the filter is off — shown next to the toggle so the filter stays
   * discoverable instead of silently swallowing rows.
   */
  noop_hidden_count?: number
  pagination: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
}

export interface JobsListParams {
  type?: string
  status?: string
  limit?: number
  offset?: number
  target_entity_type?: string
  target_entity_uid?: string
  /**
   * Hide successful runs that reported `output.processed = 0` — the org-wide
   * sweeps a write enqueues speculatively. On by default on the jobs page;
   * absent (i.e. off) everywhere else, so an entity-scoped panel still sees
   * every run of its job.
   */
  hide_noop?: boolean
}

// API functions
export const fetchJobs = async (org: string, params: JobsListParams): Promise<ListJobsResponse> => {
  const response = await (apiClient.GET as any)(`/api/v1/organizations/${org}/jobs`, {
    params: {
      query: {
        type: params.type,
        status: params.status,
        limit: params.limit || 30,
        offset: params.offset || 0,
        include: 'last_execution',
        target_entity_type: params.target_entity_type,
        target_entity_uid: params.target_entity_uid,
        hide_noop: params.hide_noop ? 'true' : undefined,
      },
    },
  })

  if (response.error) {
    throw new Error(`Failed to fetch jobs: ${JSON.stringify(response.error)}`)
  }

  return response.data as ListJobsResponse
}

export const fetchJob = async (org: string, jobUid: string): Promise<JobResponse> => {
  const response = await (apiClient.GET as any)(`/api/v1/organizations/${org}/jobs/${jobUid}`, {})

  if (response.error) {
    throw new Error(`Failed to fetch job: ${JSON.stringify(response.error)}`)
  }

  return response.data as JobResponse
}

export const fetchJobExecution = async (
  org: string,
  jobUid: string,
  executionUid: string,
  logsLimit?: number
): Promise<JobExecutionResponse> => {
  const response = await (apiClient.GET as any)(`/api/v1/organizations/${org}/jobs/${jobUid}/executions/${executionUid}`, {
    params: {
      query: {
        logs_limit: logsLimit || 100,
      },
    },
  })

  if (response.error) {
    throw new Error(`Failed to fetch job execution: ${JSON.stringify(response.error)}`)
  }

  return response.data as JobExecutionResponse
}

export const createJob = async (org: string, job: CreateJobRequest): Promise<JobResponse> => {
  const response = await (apiClient.POST as any)(`/api/v1/organizations/${org}/jobs`, {
    body: job,
  })

  if (response.error) {
    throw new Error(`Failed to create job: ${JSON.stringify(response.error)}`)
  }

  return response.data as JobResponse
}

export const cancelJob = async (org: string, jobUid: string): Promise<void> => {
  const response = await (apiClient.DELETE as any)(`/api/v1/organizations/${org}/jobs/${jobUid}`, {})

  if (response.error) {
    throw new Error(`Failed to cancel job: ${JSON.stringify(response.error)}`)
  }
}

// Job type display mapping with MDI icons
export interface JobTypeInfo {
  icon: string
  label: string
}

export const jobTypeConfig: Record<string, JobTypeInfo> = {
  'asset_freesearch':           { icon: mdiMagnify,             label: 'Asset Free Search' },
  'asset_hierarchy':            { icon: mdiSitemap,             label: 'Asset Hierarchy' },
  'asset_import':               { icon: mdiImport,              label: 'Asset Import' },
  'data_import':                { icon: mdiImport,              label: 'Data Import' },
  'asset_type_hierarchy':       { icon: mdiShape,               label: 'Asset Type Hierarchy' },
  'asset_auto_creation':        { icon: mdiRobotOutline,        label: 'Asset Auto Creation' },
  'doc_asset_extraction':       { icon: mdiRobotOutline,        label: 'Doc Asset Extraction' },
  'asset_property_propagation': { icon: mdiArrowExpandAll,      label: 'Asset Property Propagation' },
  'doc_text_extraction':        { icon: mdiTextRecognition,     label: 'Document OCR' },
  'doc_embedding':              { icon: mdiVectorCombine,       label: 'Document Vectorization' },
  'embedding_backfill':         { icon: mdiVectorCombine,       label: 'Embedding Model Backfill' },
  'doc_classification':         { icon: mdiTagOutline,          label: 'Document Classification' },
  'doc_llm_classification':     { icon: mdiTagOutline,          label: 'Document AI Classification' },
  'doc_reclassification':       { icon: mdiTagMultipleOutline,  label: 'Document Reclassification' },
  'doc_meta_extraction':        { icon: mdiDatabaseSearch,      label: 'Document Data Extraction' },
  'doc_asset_association':      { icon: mdiLinkVariant,         label: 'Document Asset Association' },
  'doc_subject_resolution':     { icon: mdiLinkVariant,         label: 'Document Subject Resolution' },
  'doc_entity_extraction':      { icon: mdiAccountGroupOutline, label: 'Document Entity Extraction' },
  'doc_extract_representations': { icon: mdiVectorPolygon,      label: 'Document Representation Extraction' },
  'doc_property_to_asset':      { icon: mdiFileArrowLeftRight,  label: 'Doc Property to Asset' },
  'property_derivation':        { icon: mdiFunction,            label: 'Property Derivation' },
  'search_sync_asset':          { icon: mdiCloudSearch,         label: 'Asset Search Indexing' },
  'search_sync_document':       { icon: mdiCloudSearch,         label: 'Document Search Indexing' },
  'search_sync_user':           { icon: mdiCloudSearch,         label: 'User Search Indexing' },
  'search_sync_issue':          { icon: mdiCloudSearch,         label: 'Issue Search Indexing' },
  'search_sync_capex_plan':     { icon: mdiCloudSearch,         label: 'CapEx Plan Search Indexing' },
  'email':                      { icon: mdiEmail,               label: 'Email' },
  'stats_calculation':          { icon: mdiChartBar,            label: 'Stats Calculation' },
  'cache_cleanup':              { icon: mdiBroom,               label: 'Cache Cleanup' },
  'jobs_cleanup':               { icon: mdiBroom,               label: 'Jobs Cleanup' },
  'ingestion_timing':           { icon: mdiTimerSand,          label: 'Ingestion Timing' },
  'upload_chunks_cleanup':      { icon: mdiBroom,               label: 'Upload Chunks Cleanup' },
  'document_upload_assemble':   { icon: mdiInboxArrowDown,      label: 'Assemble Uploaded Document' },
  'ontology_sync':              { icon: mdiSitemap,             label: 'Ontology Sync' },
  'error':                      { icon: mdiAlertOctagon,        label: 'Error (test)' },
  'compliance_check':            { icon: mdiShieldCheck,          label: 'Compliance Check' },
  'compliance_rule_draft':       { icon: mdiShieldCheck,          label: 'Rule Draft' },
  'monthly_compliance_snapshot': { icon: mdiCheckDecagram,       label: 'Monthly Compliance Snapshot' },
  'approval_issue_sync':        { icon: mdiFileDocumentOutline, label: 'Approval Issue Sync' },
  'generate_report':            { icon: mdiFileChart,           label: 'Report Generation' },
  'entity_log_backfill':        { icon: mdiShoePrint,           label: 'Activity Log Backfill' },
  'asset_geolocation_backfill': { icon: mdiMapMarker,           label: 'Asset Geolocation Backfill' },
  'pii_flag_backfill':          { icon: mdiShieldAccount,       label: 'PII Flag Backfill' },
  'post_data_update':           { icon: mdiDatabaseSync,        label: 'Post Data Update' },
  'sleep':                      { icon: mdiSleep,               label: 'Sleep (test)' },
  // The 30 types below were registered on the backend without a frontend
  // entry (spec 2026-09-23-01) — TestFrontendJobTypeConsistency in
  // internal/handlers/jobsrun fails the Go build if this map (or either
  // locale's jobs.json "types") drifts from the registry again.
  'asset_geocode':              { icon: mdiCrosshairsGps,       label: 'Asset Geocoding' },
  'contract_clause_extraction': { icon: mdiScaleBalance,        label: 'Contract Clause Extraction' },
  'contract_quality_sweep':     { icon: mdiBroom,               label: 'Contract Quality Sweep' },
  'conversation_summary':       { icon: mdiMessageText,         label: 'Conversation Summary' },
  'coverage_sweep':             { icon: mdiBroom,               label: 'Coverage Sweep' },
  'data_sweep':                 { icon: mdiBroom,               label: 'Data Sweep' },
  'deposit_extract':            { icon: mdiZipBox,              label: 'Deposit Extraction' },
  'doc_address_consistency':    { icon: mdiMapMarkerCheck,      label: 'Document Address Consistency' },
  'doc_bundle_detection':       { icon: mdiCallSplit,           label: 'Bundle Detection' },
  'doc_provenance_resolve':     { icon: mdiFileFind,            label: 'Document Provenance Resolution' },
  'document_column_backfill':   { icon: mdiTableColumn,         label: 'Document Column Backfill' },
  'document_sweep':             { icon: mdiBroom,               label: 'Document Sweep' },
  'equipment_tco':              { icon: mdiFinance,             label: 'Equipment Total Cost of Ownership' },
  'meter_consumption':          { icon: mdiFinance,             label: 'Meter consumption' },
  'floorplan_calibrate':        { icon: mdiRulerSquare,         label: 'Floorplan Calibration' },
  'floorplan_digitize':         { icon: mdiFloorPlan,           label: 'Floorplan Digitization' },
  'import_extraction':          { icon: mdiZipBox,              label: 'Import Extraction' },
  'import_type_proposal_accept': { icon: mdiCallSplit,          label: 'Import Type Creation' },
  'insurance_sweep':            { icon: mdiBroom,               label: 'Insurance Sweep' },
  'kpi_facts':                  { icon: mdiChartLine,           label: 'KPI Facts' },
  'kpi_snapshot':               { icon: mdiChartBox,            label: 'KPI Snapshot' },
  'lease_sweep':                { icon: mdiBroom,               label: 'Lease Sweep' },
  'rent_index_import':          { icon: mdiChartLine,           label: 'Rent Index Import' },
  'signature_seal':             { icon: mdiSignatureFreehand,   label: 'Signature Seal' },
  'maintenance_schedule':       { icon: mdiCalendarClock,       label: 'Maintenance Schedule' },
  'message_transcription':      { icon: mdiWaveform,            label: 'Message Transcription' },
  'message_triage':             { icon: mdiInboxArrowDown,      label: 'Message Triage' },
  'knowledge_suggest':          { icon: mdiInboxArrowDown,      label: 'Knowledge Suggestion' },
  'derivation_calendar_sweep':  { icon: mdiCalendarClock,       label: 'Derivation Calendar Sweep' },
  'occupancy_boundary_sweep':   { icon: mdiBroom,               label: 'Occupancy Boundary Sweep' },
  'rental_application_purge':   { icon: mdiBroom,               label: 'Rental Application Purge' },
  'viewing_reminders':          { icon: mdiCalendarClock,       label: 'Viewing Reminders' },
  'webhook_deliver':            { icon: mdiInboxArrowDown,      label: 'Webhook Delivery' },
  'lettability_recheck':        { icon: mdiBroom,               label: 'Lettability Recheck' },
  'parcel_geo_enrich':          { icon: mdiMap,                 label: 'Parcel Geo Enrichment' },
  'property_normalization':     { icon: mdiTableSync,           label: 'Property Normalization' },
  'search_sync_contract':       { icon: mdiCloudSearch,         label: 'Contract Search Indexing' },
  'search_sync_tag':            { icon: mdiCloudSearch,         label: 'Tag Search Indexing' },
  'service_sweep':              { icon: mdiBroom,               label: 'Service Sweep' },
  'warranty_attestation_sweep': { icon: mdiBroom,               label: 'Warranty Attestation Sweep' },
  'whatsapp_media_fetch':       { icon: mdiWhatsapp,            label: 'WhatsApp Media Fetch' },
  'work_order_sweep':           { icon: mdiBroom,               label: 'Work Order Sweep' },
  'issue_follow_up_sweep':      { icon: mdiBroom,               label: 'Ticket Follow-up Sweep' },
  'scenario_engine':            { icon: mdiBroom,               label: 'Remediation Scenario Engine' },
  'email_digest':               { icon: mdiEmailFastOutline,    label: 'Daily E-mail Digest' },
  'retention_sweep':            { icon: mdiBroom,               label: 'Retention Sweep' },
  'resident_grants_sync':       { icon: mdiShieldAccount,       label: 'Resident Grants Sync' },
  'resident_grants_sweep':      { icon: mdiShieldAccount,       label: 'Resident Grants Sweep' },
  'erasure_execute':            { icon: mdiBroom,               label: 'Erasure' },
  'billing_usage_snapshot':     { icon: mdiBroom,               label: 'Billing Usage Snapshot' },
  'billing_close_month':        { icon: mdiBroom,               label: 'Billing Close Month' },
  'campaign_notify':            { icon: mdiBroom,               label: 'Campaign Agent Notification' },
  'campaign_apply':             { icon: mdiBroom,               label: 'Campaign Apply' },
  'agent_run_finalize':         { icon: mdiRobotOutline,        label: 'Agent Run Finalize' },
}

// Flat label mapping for dropdowns and simple text contexts
export const jobTypeMapping: Record<string, string> = Object.fromEntries(
  Object.entries(jobTypeConfig).map(([key, { label }]) => [key, label])
)

export const getJobTypeName = (type: string): string => {
  return jobTypeConfig[type]?.label || type
}

export const getJobTypeIcon = (type: string): string | undefined => {
  return jobTypeConfig[type]?.icon
}

// React Query hooks
//
// live?: pass useScopeLive({ entity: 'jobs' }) (list/status) or
// useScopeLive({ entity: 'jobs', uid: jobUid }) (detail) from the caller.
// While true, the jobs:* WS scope is subscribed AND acked, so polling
// relaxes to a 30s safety net instead of the normal active-job cadence — WS
// hints (migration 094_job_events_notify) handle real-time freshness;
// polling only needs to catch a missed hint. Defaults to false (unchanged
// behavior) for callers that don't pass it — e.g. AssetsImportPage.tsx and
// ExtractionManifestSummary.tsx, which query a filtered subset of jobs
// rather than driving a jobs:* subscription of their own.
export const useJobs = (org: string, params: JobsListParams, live = false) => {
  return useQuery({
    queryKey: ['jobs', org, params],
    queryFn: () => fetchJobs(org, params),
    refetchInterval: (query) => {
      // Poll every 5 seconds if there are active jobs (30s while live)
      const data = query.state.data as ListJobsResponse | undefined
      const hasActiveJobs = data?.jobs.some((job: JobResponse) =>
        !job.final_status || job.executions?.some((exec: JobExecutionResponse) =>
          exec.status === 'pending' || exec.status === 'running'
        )
      )
      if (!hasActiveJobs) return false
      return live ? 30000 : 5000
    },
  })
}

export const useJob = (org: string, jobUid: string, live = false) => {
  return useQuery({
    queryKey: ['job', org, jobUid],
    queryFn: () => fetchJob(org, jobUid),
    refetchInterval: (query) => {
      // Poll if job has no final status or has running executions (30s while live)
      const data = query.state.data as JobResponse | undefined
      const hasActiveExecutions = !data?.final_status || data?.executions?.some((exec: JobExecutionResponse) =>
        exec.status === 'pending' || exec.status === 'running'
      )
      if (!hasActiveExecutions) return false
      return live ? 30000 : 5000
    },
  })
}

// useJobExecution deliberately has NO `live` param and keeps its
// unconditional 2s poll while running — execution LOG LINES
// (job_execution_logs) have no WS hints at all (out of scope per
// docs/apis/events.md: per-log-line notifies would be too chatty), and this
// hook is what streams them, so live scopes elsewhere don't change its
// freshness story.
export const useJobExecution = (org: string, jobUid: string, executionUid: string, logsLimit?: number) => {
  return useQuery({
    queryKey: ['jobExecution', org, jobUid, executionUid, logsLimit],
    queryFn: () => fetchJobExecution(org, jobUid, executionUid, logsLimit),
    refetchInterval: (query) => {
      // Poll if execution is still running
      const data = query.state.data as JobExecutionResponse | undefined
      return data?.status === 'pending' || data?.status === 'running' ? 2000 : false
    },
  })
}

export const useCreateJob = (org: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (job: CreateJobRequest) => createJob(org, job),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', org] })
    },
  })
}

// AIProviderState is the deployment credential's health, not an org's
// (spec 2026-09-10-03 §3). One key serves every organization, so one dead key
// stops everyone.
export type AIProviderState = 'ok' | 'missing_key' | 'rejected_key'

export interface AIProviderStatus {
  state: AIProviderState
  since?: string | null
  // The provider's own rejection code, e.g. API_KEY_INVALID. Only in
  // rejected_key.
  reason?: string
  // Pending AI jobs plus documents still flagged for LLM classification.
  deferred_jobs: number
  // A rejected key is retried at most once per 15 minutes; a success resumes
  // the pipeline with no restart.
  next_probe_at?: string | null
}

export interface JobsStatusResponse {
  pending_count?: number
  done_count?: number
  total_count?: number
  // blocking_count counts processing *blockers* (human-actionable items), not
  // blocked items. See useJobsBlockers for the per-blocker detail.
  blocking_count?: number
  ai_provider?: AIProviderStatus
}

export const fetchJobsStatus = async (org: string): Promise<JobsStatusResponse> => {
  const response = await (apiClient.GET as any)(`/api/v1/organizations/${org}/jobs/status`, {
    params: {
      query: {
        include: 'done_count,total_count,blocking_count,ai_provider',
        period: '5m',
      },
    },
  })

  if (response.error) {
    throw new Error(`Failed to fetch jobs status: ${JSON.stringify(response.error)}`)
  }

  return response.data as JobsStatusResponse
}

// Machine-readable blocker codes (spec 2026-08-18-01 §2). The API never sends
// copy or URLs — these codes key both the i18n strings and the CTA route.
export type BlockerKind = 'document_type_proposal' | 'missing_api_key' | 'invalid_api_key'
export type BlockerAction = 'review_proposal' | 'configure_gemini_key'

export interface JobsBlocker {
  kind: BlockerKind
  action: BlockerAction
  entity_type?: string | null
  entity_uid?: string | null
  entity_name?: string | null
  blocked_count: number
  since?: string | null
  // Machine-readable cause when the kind has one worth naming — the
  // provider's API_KEY_INVALID on invalid_api_key, for instance.
  reason?: string
}

export interface JobsBlockersResponse {
  blockers: JobsBlocker[]
}

export const fetchJobsBlockers = async (org: string): Promise<JobsBlockersResponse> => {
  const response = await (apiClient.GET as any)(`/api/v1/organizations/${org}/jobs/blockers`)

  if (response.error) {
    throw new Error(`Failed to fetch processing blockers: ${JSON.stringify(response.error)}`)
  }

  return response.data as JobsBlockersResponse
}

// useJobsBlockers lists the org's processing blockers. Pair it with
// useLiveSubscription({ entity: 'jobs' }) so resolving a proposal makes the
// row disappear without a manual reload; `live` downshifts the polling
// interval to a safety net once the jobs scope is acked.
export const useJobsBlockers = (org: string, live = false) => {
  return useQuery({
    queryKey: ['jobsBlockers', org],
    queryFn: () => fetchJobsBlockers(org),
    refetchInterval: live ? 30000 : 5000,
  })
}

export const useJobsStatus = (org: string, live = false) => {
  return useQuery({
    queryKey: ['jobsStatus', org],
    queryFn: () => fetchJobsStatus(org),
    refetchInterval: live ? 30000 : 5000,
  })
}

export const useCancelJob = (org: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (jobUid: string) => cancelJob(org, jobUid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs', org] })
      queryClient.invalidateQueries({ queryKey: ['job', org] })
    },
  })
}